import React, { useContext, useEffect, useState } from "react";
import SyntaxHighlighter from "react-syntax-highlighter";
import { a11yDark } from "react-syntax-highlighter/dist/esm/styles/hljs";
import { MdArrowForwardIos, MdArrowBackIosNew } from "react-icons/md";
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

import { AppContext } from "./context";
import toast from "react-hot-toast";

export default ({}) => {
  const context = useContext(AppContext);

  useEffect(() => {
    document.querySelector("body").style.overflow = "hidden";
    return () => (document.querySelector("body").style.overflow = "auto");
  }, []);

  const handlePrintPDF = async () => {
    try {
      const footer = document.querySelector('.fixed');
      const body = document.querySelector('body');
      const originalFooterPosition = footer.style.position;
      const originalBodyOverflow = body.style.overflow;
      
      footer.style.position = 'relative';
      body.style.overflow = 'visible';

      const content = document.querySelector('#workshop-content');
      const canvas = await html2canvas(content, {
        scrollY: -window.scrollY,
        windowHeight: document.documentElement.scrollHeight,
        scale: 2,
        useCORS: true,
        logging: false,
        allowTaint: true,
        onclone: (clonedDoc) => {
          const images = clonedDoc.getElementsByTagName('img');
          return Promise.all(Array.from(images).map(img => {
            if (img.complete) return Promise.resolve();
            return new Promise((resolve) => {
              img.onload = resolve;
              img.onerror = resolve;
            });
          }));
        }
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: 'a4',
        compress: true
      });

      const imgProps = pdf.getImageProperties(imgData);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

      const pageHeight = pdf.internal.pageSize.getHeight();
      let heightLeft = pdfHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, pdfHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - pdfHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, pdfHeight);
        heightLeft -= pageHeight;
      }

      pdf.save('workshop.pdf');

      footer.style.position = originalFooterPosition;
      body.style.overflow = originalBodyOverflow;

      toast.success('PDF generated successfully');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF');
    }
  };

  const Item = ({ element }) => {
    if (element.kind == "text") {
      return <div className="rich-text text-sm md:text-base leading-relaxed text-gray-800" dangerouslySetInnerHTML={{ __html: element.value }} />;
    } else if (element.kind == "image") {
      return (
        <div className="mb-4 text-center w-full">
          <img src={element.value?.rawBody || element.value} className="max-w-fit rounded-xl w-full mx-auto shadow-sm" />
        </div>
      );
    } else if (element.kind == "code") {
      return (
        <div className="mb-4 w-full rounded-xl overflow-hidden shadow-sm">
          <SyntaxHighlighter language="javascript" showLineNumbers={true} showInlineLineNumbers={true} style={a11yDark} customStyle={{ borderRadius: "0.75rem", fontSize: "0.8rem", padding: "1.25rem" }}>
            {element.value}
          </SyntaxHighlighter>
        </div>
      );
    } else if (element.kind == "video") {
      return (
        <div className="mb-4 w-full rounded-xl overflow-hidden max-w-4xl mx-auto shadow-sm">
          <video
            src={element.value}
            frameBorder="0"
            className="h-full w-full rounded-xl aspect-video"
            controls
            allowFullScreen></video>
        </div>
      );
    }

    return <div>No component</div>;
  };

  const Footer = () => {
    const totalSteps = context.workshop.steps.length;

    useEffect(() => {
      function handleSpaceBar(evt) {
        if (document.activeElement.tagName === "TEXTAREA" || document.activeElement.tagName === "INPUT") return;
        if (evt.key == " " || evt.code == "Space" || evt.code == 32) onContinue();
      }
      document.body.addEventListener("keyup", handleSpaceBar);
      return () => document.body.removeEventListener("keyup", handleSpaceBar);
    }, []);

    const onContinue = () => {
      context.next();
    };

    return (
      <div className="fixed bottom-0 inset-x-0 bg-white/95 backdrop-blur-sm border-t border-gray-200 shadow-lg z-50">
        <div className="flex items-center justify-between max-w-5xl mx-auto px-6 py-3">
          <div className="text-sm text-gray-500 font-medium hidden sm:block">
            Step {context.index} of {totalSteps}
          </div>

          <div className="flex items-center gap-3 mx-auto sm:mx-0">
            {context.index >= 2 && (
              <button
                onClick={() => context.setIndex(context.index - 1)}
                className="group flex gap-2 items-center bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium px-5 py-2.5 rounded-xl transition-colors duration-200">
                <MdArrowBackIosNew className="text-sm group-hover:-translate-x-1 transition-transform" />
                <span className="hidden sm:inline">Previous</span>
              </button>
            )}
            <button
              className="group flex gap-2 items-center bg-blue-600 hover:bg-blue-700 text-white font-medium px-6 py-2.5 rounded-xl transition-colors duration-200 shadow-sm"
              onClick={onContinue}>
              {context.index === totalSteps ? "Finish" : "Continue"}
              <MdArrowForwardIos className="text-sm group-hover:translate-x-1 transition-transform" />
            </button>
          </div>

          <div className="hidden sm:flex items-center gap-1.5 text-xs text-gray-400">
            <kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-[10px] font-mono border border-gray-200">Space</kbd>
            <span>to continue</span>
          </div>
        </div>
      </div>
    );
  };

  const arr = [];

  for (let j = 0; j < context.workshop.steps.length; j++) {
    const step = context.workshop.steps[j];
    arr.push(
      <h2 key={`title-${j}`} className="text-xl md:text-2xl font-bold text-gray-900 mb-4 mt-8 pb-3 border-b border-gray-100">
        <span className="text-blue-600 mr-2">{j + 1}.</span>
        {step.title}
      </h2>
    );
    for (let i = 0; i < step.elements.length; i++) {
      arr.push(<Item key={`item-${j}-${i}`} element={step.elements[i]} />);
    }
  }

  return (
    <>
      <div id="workshop-content" className="w-full max-w-3xl mx-auto">
        <div className="flex justify-end mb-4">
          <button
            onClick={handlePrintPDF}
            className="inline-flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-xl text-sm font-medium transition-colors">
            Print as PDF
          </button>
        </div>
        <div className="space-y-4">{arr}</div>
      </div>
      <Footer />
    </>
  );
};
